// ReceiptDrawer — the single editable surface for a material receipt.
//
// Replaces JobTagPanel + JobTagModal. Category-aware (General P/I/CA vs
// Regulatory R). The matching card is read-only and renders from the same draft;
// every edit happens here. Opening it pairs with the PdfViewerPanel (handled by
// AcePage) so the operator fills fields against the scanned job tag.

import React, { useEffect } from 'react';
import { docTypesFor, CATEGORY_META, isRegulatory, type JobCategory } from '../../data/docTypes';
import { MAX_JOBS, noteStamp, type ReceiptDraft, type DraftJob, type DraftDoc } from '../../data/receiptDraft';

// Internal job-type codes (distinct from the receipt category). Placeholder set.
const JOB_TYPE_CODES = ['Y', 'P', 'I'];

const FIELD = 'w-full px-2.5 py-1.5 rounded-md border border-trim text-sm text-ink outline-none focus:border-teal-400 bg-white';
const NUM = 'w-full px-2 py-1.5 rounded-md border border-trim text-sm text-center text-ink outline-none focus:border-teal-400 bg-white';
const LBL = 'block text-xs font-semibold text-copy uppercase tracking-wide mb-0.5';

const JOB_STATUS: Record<'general' | 'R', string[]> = {
  general: ['Received', 'Sample', 'Mailing', 'Complete', 'Aborted'],
  R: ['CS-X', 'PSD', 'TSR', 'X'],
};

// Allowed outbound mail-envelope sizes, by category.
const MAIL_ENV_SIZES: Record<JobCategory, string[]> = {
  R: ['#10', '6x9', '6x9 Flat', '9x12'],
  P: ['6x9', '9x12'],
  I: ['6x9', '9x12'],
  CA: ['#10', '9x12'],
};

function Section({ title, children, order }: { title: string; children: React.ReactNode; order?: number }) {
  return (
    <div className="mb-4" style={order != null ? { order } : undefined}>
      <div className="text-xs font-semibold text-teal-600 uppercase tracking-wider mb-2 pb-1 border-b border-tint">{title}</div>
      {children}
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className={LBL}>{label}</label>{children}</div>;
}

export function ReceiptDrawer({ open, draft, author, onChange, onClose, onSave }: {
  open: boolean;
  draft: ReceiptDraft | null;
  author: string;
  onChange: (patch: Partial<ReceiptDraft>) => void;
  onClose: () => void;
  onSave: () => void;
}) {
  // Esc closes the drawer (only while it's open).
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!draft) {
    return <div className="fixed top-0 right-0 h-full z-[112] transition-transform duration-300" style={{ width: 576, transform: 'translateX(calc(100% + 2px))' }} />;
  }
  const cat = draft.category as JobCategory;
  const reg = isRegulatory(cat);
  const docOptions = docTypesFor(cat);
  const statusOptions = reg ? JOB_STATUS.R : JOB_STATUS.general;
  // Section order differs by category:
  //   P/CA/I: Match > Receipt Logistics > Documents Received > Notes
  //   R:      Match > Documents Received > Regulatory > Receipt Logistics > Notes
  const ord = reg
    ? { match: 1, docs: 2, reg: 3, logistics: 4, notes: 5 }
    : { match: 1, logistics: 2, docs: 3, reg: 0, notes: 4 };

  const setJob = (i: number, patch: Partial<DraftJob>) =>
    onChange({ jobs: draft.jobs.map((j, idx) => idx === i ? { ...j, ...patch } : j) });
  const addJob = () => {
    if (draft.jobs.length >= MAX_JOBS) return;
    onChange({ jobs: [...draft.jobs, { jobNumber: '', issuerName: '', jobType: '', jobStatus: '', jobIsNaa: null, ordered: {} }] });
  };
  const removeJob = (i: number) =>
    onChange({ jobs: draft.jobs.length > 1 ? draft.jobs.filter((_, idx) => idx !== i) : draft.jobs });
  const setDoc = (i: number, patch: Partial<DraftDoc>) =>
    onChange({ receivedDocs: draft.receivedDocs.map((d, idx) => idx === i ? { ...d, ...patch } : d) });
  const addDoc = () => onChange({ receivedDocs: [...draft.receivedDocs, { type: '', qty: 0 }] });
  const removeDoc = (i: number) => onChange({ receivedDocs: draft.receivedDocs.length > 1 ? draft.receivedDocs.filter((_, idx) => idx !== i) : draft.receivedDocs });
  // Ordered binds to the primary job (multi-job splits live in the Allocation drill-down).
  const setOrdered = (type: string, v: string) => {
    const qty = Math.max(0, parseInt(v, 10) || 0);
    onChange({ jobs: draft.jobs.map((j, idx) => idx === 0 ? { ...j, ordered: { ...j.ordered, [type]: qty } } : j) });
  };

  return (
    <div
      className="fixed top-0 right-0 h-full z-[112] bg-white border-l border-trim flex flex-col transition-transform duration-300"
      style={{ width: 576, transform: open ? 'translateX(0)' : 'translateX(calc(100% + 2px))', boxShadow: open ? '-4px 0 24px rgba(4,50,67,.12)' : 'none' }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-trim flex-shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-sm font-semibold text-ink">Material Receipt</span>
          <span className={CATEGORY_META[cat].tp + ' !text-[11px]'}>{CATEGORY_META[cat].label}</span>
          <span className="text-xs text-muted truncate">· {draft.scan}</span>
        </div>
        <button onClick={onClose} className="w-6 h-6 flex items-center justify-center rounded-md text-muted hover:bg-wash hover:text-ink">
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
        </button>
      </div>

      {/* Body — section order is category-driven via the `order` prop (flex-col). */}
      <div className="flex-1 overflow-y-auto px-4 py-3 flex flex-col">
        {/* Match */}
        <Section title="Match" order={ord.match}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="col-span-2">
              <Field label="Client name(s) — comma separated">
                <input className={FIELD} value={draft.clientNames.join(', ')} onChange={e => onChange({ clientNames: e.target.value.split(',').map(s => s.trim()).filter(Boolean) })} />
              </Field>
            </div>
            {reg ? (
              <Field label="Effective Date · manual"><input className={FIELD} placeholder="YYYY-MM-DD" value={draft.effectiveDate} onChange={e => onChange({ effectiveDate: e.target.value })} /></Field>
            ) : (
              <>
                <Field label="Event Date"><input className={FIELD} placeholder="YYYY-MM-DD" value={draft.eventDate} onChange={e => onChange({ eventDate: e.target.value })} /></Field>
                <Field label="Record Date"><input className={FIELD} placeholder="YYYY-MM-DD" value={draft.recordDate} onChange={e => onChange({ recordDate: e.target.value })} /></Field>
              </>
            )}
          </div>
          {/* Jobs (looked up in MIC) — up to 3 for P/CA/I; R is 1:1. */}
          {draft.jobs.map((j, i) => (
            <div key={i} className="rounded-md border border-trim p-2 mb-2">
              <div className="flex items-center justify-between mb-1">
                <span className="text-xs font-semibold text-copy">Job {i + 1}</span>
                {draft.jobs.length > 1 && (
                  <button onClick={() => removeJob(i)} className="text-slate-300 hover:text-red-500 text-xs" title="Remove job">✕</button>
                )}
              </div>
              <div className="grid grid-cols-2 gap-2 mb-2">
                <Field label="Job #"><input className={FIELD} placeholder="e.g. 3010902" value={j.jobNumber} onChange={e => setJob(i, { jobNumber: e.target.value })} /></Field>
                <Field label="Job Type">
                  <select className={FIELD} value={j.jobType} onChange={e => setJob(i, { jobType: e.target.value })}>
                    <option value="">—</option>
                    {JOB_TYPE_CODES.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </Field>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <Field label="Job Status">
                  <select className={FIELD} value={j.jobStatus} onChange={e => setJob(i, { jobStatus: e.target.value })}>
                    <option value="">—</option>
                    {statusOptions.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                </Field>
                {!reg && (
                  <Field label="NAA">
                    <select className={FIELD} value={j.jobIsNaa == null ? '' : j.jobIsNaa ? 'Y' : 'N'} onChange={e => setJob(i, { jobIsNaa: e.target.value === '' ? null : e.target.value === 'Y' })}>
                      <option value="">—</option><option value="Y">Yes</option><option value="N">No</option>
                    </select>
                  </Field>
                )}
              </div>
            </div>
          ))}
          {!reg && draft.jobs.length < MAX_JOBS && (
            <button onClick={addJob} className="text-xs font-medium text-teal-600 hover:text-teal-700">+ job ({draft.jobs.length}/{MAX_JOBS})</button>
          )}
        </Section>

        {/* Documents received — list-guided but free-typeable (combobox). */}
        <Section title="Documents received" order={ord.docs}>
          <datalist id={`doctypes-${cat}`}>
            {docOptions.map(o => <option key={o} value={o} />)}
          </datalist>
          <div className="grid gap-2 mb-1" style={{ gridTemplateColumns: '1fr 70px 70px 22px' }}>
            <span className={LBL + ' !mb-0'}>Type</span>
            <span className={LBL + ' !mb-0 text-center'}>Received</span>
            <span className={LBL + ' !mb-0 text-center'}>Ordered</span>
            <span />
          </div>
          {draft.receivedDocs.map((d, i) => (
            <div key={i} className="grid gap-2 mb-1.5 items-center" style={{ gridTemplateColumns: '1fr 70px 70px 22px' }}>
              <input className={FIELD} list={`doctypes-${cat}`} value={d.type} placeholder="select or type…"
                onChange={e => setDoc(i, { type: e.target.value })} />
              <input type="number" min="0" className={NUM} value={d.qty} onChange={e => setDoc(i, { qty: Math.max(0, parseInt(e.target.value, 10) || 0) })} />
              <input type="number" min="0" className={NUM} value={draft.jobs[0]?.ordered[d.type] ?? 0} onChange={e => setOrdered(d.type, e.target.value)} />
              <button onClick={() => removeDoc(i)} className="text-slate-300 hover:text-red-500 text-xs" title="Remove">✕</button>
            </div>
          ))}
          <button onClick={addDoc} className="text-xs font-medium text-teal-600 hover:text-teal-700">+ document</button>
          {draft.jobs.length > 1 && <p className="text-[11px] text-muted mt-1">Ordered shown for job #1 — use Allocation to split across jobs.</p>}
        </Section>

        {/* Receipt logistics */}
        <Section title="Receipt logistics" order={ord.logistics}>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <Field label="Location"><input className={FIELD} value={draft.location} onChange={e => onChange({ location: e.target.value })} /></Field>
            <Field label="Distributor"><input className={FIELD} value={draft.distributor} onChange={e => onChange({ distributor: e.target.value })} /></Field>
            <Field label="Carrier"><input className={FIELD} value={draft.carrier} onChange={e => onChange({ carrier: e.target.value })} /></Field>
            <Field label="Packing Slip">
              <div className="flex gap-1">
                {(['Y', 'N'] as const).map(v => {
                  const active = (draft.hasPkSlip === true && v === 'Y') || (draft.hasPkSlip === false && v === 'N');
                  return <button key={v} type="button" onClick={() => onChange({ hasPkSlip: active ? null : v === 'Y' })} className={`flex-1 py-1.5 rounded-md border text-xs font-medium ${active ? 'bg-tint border-teal-500 text-teal-700' : 'border-trim text-copy hover:bg-wash'}`}>{v}</button>;
                })}
              </div>
            </Field>
          </div>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <Field label="Env"><input className={NUM} type="number" min="0" value={draft.pkEnvelope} onChange={e => onChange({ pkEnvelope: e.target.value })} /></Field>
            <Field label="Pkg"><input className={NUM} type="number" min="0" value={draft.pkPackages} onChange={e => onChange({ pkPackages: e.target.value })} /></Field>
            <Field label="Box"><input className={NUM} type="number" min="0" value={draft.pkBoxes} onChange={e => onChange({ pkBoxes: e.target.value })} /></Field>
            <Field label="Skid"><input className={NUM} type="number" min="0" value={draft.pkSkids} onChange={e => onChange({ pkSkids: e.target.value })} /></Field>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <Field label="Mail Env">
              <select className={FIELD} value={draft.mailEnvelopeSize} onChange={e => onChange({ mailEnvelopeSize: e.target.value })}>
                <option value="">—</option>
                {(MAIL_ENV_SIZES[cat] || ['6x9', '9x12']).map(s => <option key={s} value={s}>{s}</option>)}
                {draft.mailEnvelopeSize && !(MAIL_ENV_SIZES[cat] || []).includes(draft.mailEnvelopeSize) && (
                  <option value={draft.mailEnvelopeSize}>{draft.mailEnvelopeSize}</option>
                )}
              </select>
            </Field>
            <Field label="Sample Wt (oz)"><input className={FIELD} value={draft.sampleWeight} onChange={e => onChange({ sampleWeight: e.target.value })} /></Field>
            <Field label="Intake By"><input className={FIELD} value={draft.intakeBy} onChange={e => onChange({ intakeBy: e.target.value })} /></Field>
          </div>
        </Section>

        {/* Regulatory-only */}
        {reg && (
          <Section title="Regulatory" order={ord.reg}>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2"><Field label="Book Name"><input className={FIELD} value={draft.bookName} onChange={e => onChange({ bookName: e.target.value })} /></Field></div>
              <Field label="Material Type">
                <select className={FIELD} value={draft.materialType} onChange={e => onChange({ materialType: e.target.value })}>
                  <option value="">—</option>
                  {docOptions.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </Field>
              <Field label="Addressed To"><input className={FIELD} value={draft.addressed} onChange={e => onChange({ addressed: e.target.value })} /></Field>
              <Field label="Lit Code"><input className={FIELD} value={draft.litCode} onChange={e => onChange({ litCode: e.target.value })} /></Field>
              <Field label="CUSIP"><input className={FIELD} value={draft.cusip} onChange={e => onChange({ cusip: e.target.value })} /></Field>
              <Field label="Ticker"><input className={FIELD} value={draft.ticker} onChange={e => onChange({ ticker: e.target.value })} /></Field>
              <Field label="Quantity"><input className={NUM} type="number" min="0" value={draft.quantity} onChange={e => onChange({ quantity: e.target.value })} /></Field>
              <Field label="Post-Sale">
                <div className="flex gap-1">
                  {([{ v: true, l: 'Post-Sale' }, { v: false, l: 'Interim' }] as const).map(({ v, l }) => (
                    <button key={l} type="button" onClick={() => onChange({ postSale: v })} className={`flex-1 py-1.5 rounded-md border text-xs font-medium ${draft.postSale === v ? 'bg-tint border-teal-500 text-teal-700' : 'border-trim text-copy hover:bg-wash'}`}>{l}</button>
                  ))}
                </div>
              </Field>
              <Field label="Tracking #"><input className={FIELD} value={draft.tracking} onChange={e => onChange({ tracking: e.target.value })} /></Field>
              <Field label="FFC Only"><input className={FIELD} value={draft.ffcOnly} onChange={e => onChange({ ffcOnly: e.target.value })} /></Field>
              <div className="col-span-2"><Field label="Error Code"><input className={FIELD} value={draft.errorCode} onChange={e => onChange({ errorCode: e.target.value })} /></Field></div>
            </div>
          </Section>
        )}

        <Section title="Notes" order={ord.notes}>
          {draft.noteLog.length > 0 && (
            <div className="space-y-2 mb-2">
              {draft.noteLog.map((n, i) => (
                <div key={i} className="rounded-md border border-trim bg-wash px-2.5 py-1.5">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="text-xs font-semibold text-copy">{n.author}</span>
                    <span className="text-[11px] text-muted">{n.timestamp}</span>
                  </div>
                  <div className="text-sm text-ink whitespace-pre-wrap">{n.text}</div>
                </div>
              ))}
            </div>
          )}
          <textarea className={FIELD + ' resize-none'} rows={2} placeholder="Add a note…" value={draft.newNote} onChange={e => onChange({ newNote: e.target.value })} />
          <button
            type="button"
            disabled={!draft.newNote.trim()}
            onClick={() => onChange({ noteLog: [...draft.noteLog, { author, timestamp: noteStamp(), text: draft.newNote.trim(), kind: 'note' }], newNote: '' })}
            className="mt-1 text-xs font-medium text-teal-600 hover:text-teal-700 disabled:opacity-40"
          >
            + add note
          </button>
        </Section>
      </div>

      {/* Footer */}
      <div className="px-4 pb-4 pt-2.5 border-t border-trim flex gap-2.5 flex-shrink-0">
        <button type="button" onClick={onClose} className="flex-1 py-1.5 rounded-lg border border-trim text-xs font-medium text-copy hover:bg-wash">Close</button>
        <button type="button" onClick={onSave} className="flex-1 py-1.5 rounded-lg text-xs font-medium text-white bg-gradient-to-r from-teal-500 to-cyan-500 hover:shadow-md">Save</button>
      </div>
    </div>
  );
}
